import {forecastJobStreamUrl} from '../../../../../utils/api/aiCitationAnalysis';

const FORECAST_EVENT_NAMES = ['forecast.phase', 'forecast.completed', 'forecast.failed', 'forecast.cancelled'];

function parseSsePayload(rawData) {
    if (!rawData) {
        return {};
    }
    try {
        return JSON.parse(rawData);
    } catch (_) {
        return {};
    }
}

/**
 * MINSKY-16818 (ticket 02): subscribe to a forecast job's live phase-event stream, published by
 * the backend via {@code AiChatSseBroker} (same broker/endpoint shape as AI Assistant Chat's
 * {@code runStream.js} — reused directly, not reimplemented). Late subscribers (e.g. a tab
 * reattaching after reload) receive buffered events first, then live events.
 *
 * @param {string} jobId
 * @param {object} handlers
 * @param {(payload: {phase: string}) => void} [handlers.onPhase]
 * @param {(payload: object) => void} [handlers.onCompleted]
 * @param {(payload: {error?: string}) => void} [handlers.onFailed]
 * @param {(payload: object) => void} [handlers.onCancelled]
 * @param {() => void} [handlers.onOpen] - stream successfully established
 * @param {() => void} [handlers.onUnavailable] - stream could not be established at all
 * @param {(EventSource: typeof EventSource)} [EventSourceImpl] - injection seam for tests
 * @returns {() => void} unsubscribe function
 */
export function subscribeToForecastJobStream(jobId, handlers = {}, EventSourceImpl = EventSource) {
    const {onPhase, onCompleted, onFailed, onCancelled, onOpen, onUnavailable} = handlers;

    const source = new EventSourceImpl(forecastJobStreamUrl(jobId));
    let opened = false;

    source.onopen = () => {
        opened = true;
        onOpen?.();
    };

    source.onerror = () => {
        // EventSource retries transport errors on its own; only treat this as "unavailable" if
        // we never got as far as opening the connection (ticket 02: fall back to a static label
        // rather than erroring when the stream can't be established at all, e.g. proxy blocks SSE).
        if (!opened) {
            onUnavailable?.();
        }
    };

    const dispatch = (eventName, callback) => (event) => {
        callback?.(parseSsePayload(event?.data));
    };

    source.addEventListener('forecast.phase', dispatch('forecast.phase', onPhase));
    source.addEventListener('forecast.completed', dispatch('forecast.completed', onCompleted));
    source.addEventListener('forecast.failed', dispatch('forecast.failed', onFailed));
    source.addEventListener('forecast.cancelled', dispatch('forecast.cancelled', onCancelled));

    return () => {
        source.close();
    };
}

export {FORECAST_EVENT_NAMES};
